import { useCallback, useRef } from 'react';

import type { ReportAttachment } from '@/features/guardian-profile/schemas/guardian-report-issue-schema';

import type {
  ReportAttachmentPickerMediaType,
  ReportAttachmentPickerOptions,
} from './report-attachment-picker-types';

export type { ReportAttachmentPickerMediaType, ReportAttachmentPickerOptions };

/**
 * Soft ceiling so a huge multi-select can't blow memory with blob URLs. Count / size rules are
 * enforced in form validation — the OS file dialog cannot cap selection to 5.
 */
const WEB_PICK_SOFT_CAP = 20;

/**
 * Web file picker for bug-report attachments.
 *
 * `react-native-image-picker`'s web build rejects anything other than `mediaType: 'photo'`
 * ("only photo mediaType is supported for web"), so `mixed` never opens the dialog. Drive a
 * native `<input type="file">` instead so guardians can pick images *and* videos like native.
 *
 * Uses `blob:` object URLs (not `FileReader` data-URLs): large video `data:` URIs routinely fail
 * to play in `<video>` / `expo-video` on web.
 *
 * Unlike native, the browser dialog cannot limit how many files are selected — we keep the full
 * selection (up to {@link WEB_PICK_SOFT_CAP}) and let the Evidence field validate + block submit.
 */
export function useReportAttachmentPicker() {
  const busyRef = useRef(false);

  return useCallback(
    async (options: ReportAttachmentPickerOptions): Promise<ReportAttachment[]> => {
      if (options.maxFiles <= 0 || busyRef.current) return [];
      if (typeof document === 'undefined') return [];

      busyRef.current = true;
      try {
        return await pickFiles(options.mediaType ?? 'any');
      } finally {
        busyRef.current = false;
      }
    },
    [],
  );
}

function acceptForMediaType(mediaType: ReportAttachmentPickerMediaType) {
  if (mediaType === 'photo') return 'image/*';
  if (mediaType === 'video') return 'video/*';
  return 'image/*,video/*';
}

function pickFiles(mediaType: ReportAttachmentPickerMediaType): Promise<ReportAttachment[]> {
  return new Promise(resolve => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = acceptForMediaType(mediaType);
    // Always multi — the OS won't enforce our 5-image / 1-video caps.
    input.multiple = true;
    input.style.display = 'none';
    document.body.appendChild(input);

    let settled = false;
    const finish = (attachments: ReportAttachment[]) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(attachments);
    };

    const cleanup = () => {
      input.removeEventListener('change', onChange);
      input.removeEventListener('cancel', onCancel);
      window.removeEventListener('focus', onWindowFocus);
      input.remove();
    };

    const onCancel = () => finish([]);

    // Some browsers don't fire `cancel`; when the dialog closes without a selection the window
    // regains focus with an empty `files` list — treat that as cancel after a short tick.
    const onWindowFocus = () => {
      window.setTimeout(() => {
        if (!settled && (!input.files || input.files.length === 0)) finish([]);
      }, 300);
    };

    const onChange = () => {
      const files = Array.from(input.files ?? []).slice(0, WEB_PICK_SOFT_CAP);
      if (files.length === 0) {
        finish([]);
        return;
      }

      const accepted: ReportAttachment[] = [];
      for (const file of files) {
        const attachment = fileToAttachment(file);
        const allowed =
          mediaType === 'any' ||
          (mediaType === 'photo' ? attachment.kind === 'image' : attachment.kind === 'video');
        if (!allowed) {
          try {
            URL.revokeObjectURL(attachment.uri);
          } catch {
            // ignore
          }
          continue;
        }
        accepted.push(attachment);
      }
      finish(accepted);
    };

    input.addEventListener('change', onChange);
    input.addEventListener('cancel', onCancel);
    window.addEventListener('focus', onWindowFocus);
    input.click();
  });
}

function isVideoFile(file: File) {
  if (file.type.startsWith('video/')) return true;
  // Some browsers leave `type` empty for camera roll exports — fall back to extension.
  return /\.(mp4|mov|m4v|webm|mkv|avi)$/i.test(file.name);
}

function fileToAttachment(file: File): ReportAttachment {
  const kind: ReportAttachment['kind'] = isVideoFile(file) ? 'video' : 'image';
  const mime = file.type || (kind === 'video' ? 'video/mp4' : 'image/jpeg');

  return {
    // Object URL — playable by expo-video / <video>, and fetchable for Storage upload.
    uri: URL.createObjectURL(file),
    name: file.name || (kind === 'video' ? 'video' : 'image'),
    mime,
    kind,
    fileSize: file.size,
  };
}

export default useReportAttachmentPicker;
